import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'
import { waitFor } from '@app/utils/waitFor'

import type { ApiCreditCard } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { routingDateSelector } from './routing'

export const cardSlice = createReduxSlice<{ user_id: string; loaded_at: number; card: ApiCreditCard | null }>('payment_card')

export const setCardSlice = (card: ApiCreditCard | null) => cardSlice.update(state => (state ? { ...state, loaded_at: moment().unix(), card } : state))

/** Reads the card and stores it, so a caller that polls for a freshly bound card also refreshes the slice. */
export const fetchCard = () =>
  createThunk(async dispatch => {
    const [{ getCard }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/card'), import('@app/store/actions/api')])
    const { data } = await dispatch(callV2Api(getCard()))
    dispatch(setCardSlice(data))
    return data
  })

/**
 * Waits for a card the provider binds on its own side, as Stripe's webhook does. Binding over a card
 * replaces it with a new record, so the card it replaces is not the answer.
 */
export const waitForBoundCard = (replacedId: string | null, options: { abortSignal: AbortSignal; interval?: number }) =>
  createThunk(dispatch =>
    waitFor(
      async () => {
        const card = await dispatch(fetchCard())
        return card && card.id !== replacedId ? card : null
      },
      { interval: 1000, ...options }
    )
  )

export const deleteCard = () =>
  createThunk(async dispatch => {
    const [{ deleteCard }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/card'), import('@app/store/actions/api')])
    await dispatch(callV2Api(deleteCard()))
    dispatch(setCardSlice(null))
  })

export const cardResultSelector = createSelector([cardSlice.selector, profileUserResultSelector, routingDateSelector], (slice, userResult, date) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (user?.account_type !== 'parent') return null
    if (!slice || slice.user_id !== user.id || moment.unix(slice.loaded_at).add(10, 'minutes').isBefore(date)) {
      return c.load(
        createPromise('Payment card must be loaded', [user.id, date.format()].join(':'), async dispatch => {
          const card = await dispatch(fetchCard())
          dispatch(cardSlice.set({ user_id: user.id, loaded_at: date.unix(), card }))
        })
      )
    }
    return slice.card
  })
)
